import React, { Fragment, useMemo } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import compose from 'lodash.flowright';
import { graphql } from 'react-apollo';
import { withRouter } from 'react-router';
import classNames from 'classnames';

import { getAlbumMembershipGroupNavigationQuery } from '../../../../../../../../temp/views/memberships/AlbumMembershipGroup/graphql/queries/getAlbumMembershipGroupNavigation';
import useBasename from '../../../../../../../../../components/routing/useBasename';
import {
  ALBUM_FOLDER_JOIN_TYPE,
  ALL_PHOTOS_MODE,
} from '../../../../../../../../../constants/album-folder';
import { AlbumFolderForJoinTypeCheck } from '../../../../../Matches/graphql/queries/album-folder';
import { HierarchyItemForMatches } from '../../../../../Matches/graphql/queries/hierarchy-item';
import HierarchyItemCard from '../HierarchyItemCard';
import AlbumsIcon from '../../../../../../../../../components/icons/AlbumsIcon';
import { getHierarchyItemCoverPhoto, shouldFaceblock } from './helpers';
import FeatureBlock from '../../../../../../../../../components/contextual/FeatureBlock';
import VideoGuestBookLanding from '../../../../../../AlbumFolder/WeddingEvents/VideoGuestBookLanding/VideoGuestBookLanding';
import ListingCardActions from '../ListingCardActions';
import getConfig from '../../../../../../../../../util/config';
import { isWeddingsContent } from '../../../../../../../../../util/album';
import { ALBUM_SUBTYPE } from '../../../../../../../../../constants/albums';

const propTypes = {
  albumMembershipGroup: PropTypes.shape({
    album_folder: PropTypes.shape({
      id: PropTypes.string,
    }),
  }),
  albumMemberships: PropTypes.shape({
    records: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        album: PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string,
          last_uploaded_at: PropTypes.string,
        }),
      }),
    ),
  }),
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
    children: PropTypes.shape({
      albums: PropTypes.shape({
        total: PropTypes.number,
        records: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string,
            name: PropTypes.string,
            cover_photo: PropTypes.shape({
              thumbnail_urls: PropTypes.arrayOf(
                PropTypes.shape({
                  url: PropTypes.string,
                  width: PropTypes.number,
                }),
              ),
            }),
            last_uploaded_at: PropTypes.string,
          }),
        ),
      }),
      album_folders: PropTypes.shape({
        total: PropTypes.number,
        records: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string,
            name: PropTypes.string,
            updated_at: PropTypes.string,
          }),
        ),
      }),
    }),
  }),
  entitledPurchases: PropTypes.arrayOf(PropTypes.string),
  generateLink: PropTypes.func,
  className: PropTypes.string,
  watermarkSettings: PropTypes.shape({
    wallpaperCustomText: PropTypes.string,
    wallpaperFontSize: PropTypes.number,
  }),
};

const Hierarchy = ({
  albumMemberships,
  albumFolder,
  entitledPurchases,
  generateLink,
  className,
  watermarkSettings,
}) => {
  const basename = useBasename();
  const config = getConfig();
  const now = moment();
  const membershipsWithMatches = useMemo(
    () =>
      (albumMemberships?.records ?? []).filter(
        albumMembership =>
          (albumMembership.matched_photos_extended?.total ?? 0) > 0,
      ),
    [albumMemberships],
  );

  const classes = classNames({
    'panel-main-wrap': true,
    [className]: !!className,
  });

  // Since it's possible we have a sub folder, check the root too
  const isJoinAll =
    albumFolder?.join_type === ALBUM_FOLDER_JOIN_TYPE.ALL ||
    albumFolder?.root?.join_type === ALBUM_FOLDER_JOIN_TYPE.ALL;

  const albumsWithoutMatchesOrJoins = useMemo(
    () =>
      (albumFolder?.children?.albums?.records ?? []).filter(album => {
        // If it's not join all this filter is irrelevant, as the albumFolder
        // already carries a filter to exclude individual memberships
        if (
          !isJoinAll ||
          albumFolder?.album_subtype !== ALBUM_SUBTYPE.SCHOOLS
        ) {
          return true;
        }

        // Avoids duping between albums w/ matches and the albumFolder albums.
        return !membershipsWithMatches.find(
          membership => membership.album.id === album.id,
        );
      }),
    [albumFolder],
  );

  const isPictureDay =
    albumFolder?.picture_day_enabled || albumFolder?.root?.picture_day_enabled;

  const isWebGallery =
    albumFolder?.is_web_gallery || albumFolder?.root?.is_web_gallery;

  const hasChildren =
    (albumFolder?.children?.album_folders?.total ?? 0) > 0 ||
    (albumsWithoutMatchesOrJoins.length ?? 0) > 0;

  return (
    <div className={classes}>
      {membershipsWithMatches.length > 0 &&
        albumFolder?.album_subtype === ALBUM_SUBTYPE.SCHOOLS &&
        isJoinAll && (
          <Fragment>
            <p className="text-light text-extra-bold text-sm">
              ALBUMS WITH MATCHES
            </p>
            <div
              className="content-listing-row mb-3"
              data-testid="matched-albums"
            >
              {membershipsWithMatches.map(albumMembership => {
                const coverPhoto = getHierarchyItemCoverPhoto(albumMembership);
                const faceblock = shouldFaceblock(
                  entitledPurchases,
                  coverPhoto,
                  isPictureDay,
                  isWebGallery,
                );

                return (
                  <HierarchyItemCard
                    key={albumMembership.id}
                    hierarchyItem={albumMembership}
                    faceBlock={faceblock}
                    watermarkSettings={watermarkSettings}
                    icon={<AlbumsIcon />}
                    linkTo={
                      generateLink?.(albumMembership.album.id) ??
                      `/app/${basename}/hierarchy-items/${albumMembership.album.id}`
                    }
                  />
                );
              })}
            </div>
          </Fragment>
        )}
      {(albumMemberships?.total ?? 0) > 0 && !isJoinAll && (
        <Fragment>
          <p className="text-light text-extra-bold text-sm">JOINED ALBUMS</p>
          <div className="content-listing-row mb-3" data-testid="joined-albums">
            {albumMemberships.records.map(albumMembership => {
              const coverPhoto = getHierarchyItemCoverPhoto(albumMembership);
              const faceblock = shouldFaceblock(
                entitledPurchases,
                coverPhoto,
                isPictureDay,
                isWebGallery,
              );

              return (
                <HierarchyItemCard
                  key={albumMembership.id}
                  hierarchyItem={albumMembership}
                  faceBlock={faceblock}
                  watermarkSettings={watermarkSettings}
                  linkTo={
                    generateLink?.(albumMembership.album.id) ??
                    `/app/${basename}/hierarchy-items/${albumMembership.album.id}`
                  }
                />
              );
            })}
          </div>
        </Fragment>
      )}
      {(albumFolder?.children?.album_folders?.total ?? 0) > 0 && (
        <Fragment>
          <p className="text-light text-extra-bold text-sm">FOLDERS</p>
          <div className="content-listing-row mb-3">
            {albumFolder.children.album_folders.records.map(
              childAlbumFolder => {
                const coverPhoto = getHierarchyItemCoverPhoto(childAlbumFolder);
                const faceblock = shouldFaceblock(
                  entitledPurchases,
                  coverPhoto,
                  isPictureDay,
                  isWebGallery,
                );

                return (
                  <HierarchyItemCard
                    key={childAlbumFolder.id}
                    faceBlock={faceblock}
                    watermarkSettings={watermarkSettings}
                    hierarchyItem={childAlbumFolder}
                    linkTo={
                      generateLink?.(childAlbumFolder.id) ??
                      `/app/${basename}/hierarchy-items/${childAlbumFolder.id}`
                    }
                  />
                );
              },
            )}
          </div>
        </Fragment>
      )}
      {albumsWithoutMatchesOrJoins.length > 0 && (
        <Fragment>
          <p className="text-light text-extra-bold text-sm">ALBUMS</p>
          <div className="content-listing-row mb-3" data-testid="albums">
            {albumsWithoutMatchesOrJoins.map(childAlbum => {
              const coverPhoto = getHierarchyItemCoverPhoto(childAlbum);
              const faceblock = shouldFaceblock(
                entitledPurchases,
                coverPhoto,
                isPictureDay,
                isWebGallery,
              );

              return (
                <HierarchyItemCard
                  faceBlock={faceblock}
                  watermarkSettings={watermarkSettings}
                  key={childAlbum.id}
                  hierarchyItem={childAlbum}
                  linkTo={
                    generateLink?.(childAlbum.id) ??
                    `/app/${basename}/hierarchy-items/${childAlbum.id}`
                  }
                  subtitle={
                    childAlbum.last_uploaded_at
                      ? `${now.diff(childAlbum.last_uploaded_at, 'days')}d ago`
                      : ''
                  }
                  thumbnailActions={
                    albumFolder?.all_photos_mode ===
                    ALL_PHOTOS_MODE.PRIVATE ? undefined : (
                      <ListingCardActions
                        album={childAlbum}
                        albumId={childAlbum.id}
                        url={`${config.app.baseUrl}/app/${basename}/hierarchy-items/${childAlbum.id}`}
                        quote="Check out this awesome album!"
                      />
                    )
                  }
                />
              );
            })}
          </div>
        </Fragment>
      )}
      {hasChildren && (
        <FeatureBlock condition={isWeddingsContent(albumFolder)}>
          <VideoGuestBookLanding />
        </FeatureBlock>
      )}
    </div>
  );
};

Hierarchy.propTypes = propTypes;

const withAlbumMembershipNavigation = graphql(
  getAlbumMembershipGroupNavigationQuery,
  {
    options: ({ albumMembershipGroup }) => ({
      variables: {
        id: albumMembershipGroup?.id,
      },
      fetchPolicy: 'cache-first',
    }),
    props: ({ data: { albumMembershipGroup } }) => ({
      albumMemberships: albumMembershipGroup?.album_memberships_collection,
    }),
    skip: ({ albumMembershipGroup }) => !albumMembershipGroup?.id,
  },
);

const withAlbumFolderForJoinTypeCheck = graphql(AlbumFolderForJoinTypeCheck, {
  options: ({ match }) => ({
    variables: {
      id: match.params.hierarchyItemId ?? match.params.albumMembershipGroupId,
    },
    fetchPolicy: 'cache-first',
  }),
  props: ({ data: { albumFolder } }) => ({
    albumFolderForJoinType: albumFolder,
  }),
});

const withHierarchyItem = graphql(HierarchyItemForMatches, {
  options: ({ match, albumFolderForJoinType }) => ({
    variables: {
      id: match.params.hierarchyItemId ?? match.params.albumMembershipGroupId,
      exclude_albums_with_memberships: !(
        albumFolderForJoinType?.join_type === ALBUM_FOLDER_JOIN_TYPE.ALL ||
        albumFolderForJoinType?.root?.join_type === ALBUM_FOLDER_JOIN_TYPE.ALL
      ),
    },
  }),
  props: ({ data: { hierarchyItem } }) => ({
    albumFolder: hierarchyItem?.album_folder,
  }),
  skip: ({ albumFolderForJoinType }) => !albumFolderForJoinType,
});

export { Hierarchy as HierarchyComp };

export default compose(
  withRouter,
  withAlbumMembershipNavigation,
  withAlbumFolderForJoinTypeCheck,
  withHierarchyItem,
)(Hierarchy);
